import { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ArrowLeft, Mail, CheckCircle2, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.includes('@')) return;
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: email.trim().toLowerCase() });
      setSubmitted(true);
    } catch (_e) {
      // Endpoint always returns 200 — surface success even on error
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-50 px-4">
      <div className="w-full max-w-md">
        <Link to="/login" className="inline-flex items-center gap-1 text-sm text-brand-primary hover:underline mb-6" data-testid="back-to-login">
          <ArrowLeft className="w-4 h-4" /> Back to login
        </Link>
        <Card className="border-zinc-200">
          <CardHeader>
            <CardTitle className="text-xl">Reset your password</CardTitle>
          </CardHeader>
          <CardContent>
            {submitted ? (
              <div className="text-center py-4" data-testid="forgot-success">
                <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
                <h3 className="font-semibold text-zinc-900 mb-1">Check your inbox</h3>
                <p className="text-sm text-zinc-600">
                  If <span className="font-mono">{email}</span> is a registered account, we&apos;ve sent a reset link.
                  It&apos;ll expire in 1 hour.
                </p>
                <Link to="/login">
                  <Button variant="outline" className="rounded-md mt-5" data-testid="forgot-done">Back to login</Button>
                </Link>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4" data-testid="forgot-form">
                <p className="text-sm text-zinc-600">
                  Enter your work email and we&apos;ll send you a link to reset your password.
                </p>
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-zinc-700">Email</label>
                  <div className="relative">
                    <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                    <Input
                      type="email" autoFocus required
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      placeholder="you@company.com"
                      className="rounded-md pl-9"
                      data-testid="forgot-email"
                    />
                  </div>
                </div>
                <Button type="submit" disabled={loading} className="w-full rounded-md bg-brand-primary hover:bg-brand-primary/90 text-white" data-testid="forgot-submit">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Send reset link'}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
